import React, { useState, useEffect, useRef } from 'react'
import useResizeObserver from '@react-hook/resize-observer'
import PropTypes from 'prop-types'
import Message from './message'
import TemplatesModal from '../templates-modal'
import Dropzone from 'react-dropzone'

const FirestoreChannel = props => {
  const [file, setFile] = useState(null)
  const [fileSrc, setFileSrc] = useState('')
  const [fileRatio, setFileRatio] = useState(1.0)
  const [stopScroll, setStopScroll] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [firstSeen, setFirstSeen] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [dropzoneActive, setDropzoneActive] = useState(false)
  const [message, setMessage] = useState('')

  const messagesRef = useRef()
  const fileInputRef = useRef()
  const composeRef = useRef()
  const { channelId, adminPlayerId } = props

  const useScroll = () => {
    if (props.messages.length < 1) return

    const box = messagesRef.current

    if (!firstSeen) props.setScrollAndLimit({scroll: 'bottom', limit: props.limit})

    if (props.scroll == 'bottom') {
      box.scrollTop = box.scrollHeight
      if (firstSeen) props.setScrollAndLimit({scroll: undefined, limit: props.limit})
    }
    if (props.scroll == 'middle') {
      box.scrollTop = box.scrollHeight / props.messages.length * 9
      props.setScrollAndLimit({scroll: undefined, limit: props.limit})
    }
  }

  useEffect(() => {
    channelId && props.loadMessages({id: channelId})
  }, [])

  useEffect(() => {
    setIsLoading(false)
    useScroll()
  }, [props.messages])

  useResizeObserver(messagesRef.current, (entry) => useScroll())

  if (!channelId) return null

  const messages = props.messages || []
  const props_players = props.players || []
  const players = props_players.reduce((acc, a) => { acc[a.id] = a; return acc }, {})

  const sanitize = str => { return str ? str : '' }

  const onWheelCapture = e => {
    setFirstSeen(true)
    if (!stopScroll && e.currentTarget.scrollTop == 0 && e.deltaY < 0 && !props.messages[0].payload.text.includes('Welcome to the Plei Game Chat')) {
      setStopScroll(true)
      props.setScrollAndLimit({scroll: 'middle', limit: props.limit + 10})
      props.loadMessages({ id: channelId })

      setTimeout(() => { setStopScroll(false) }, 500)
    }
  }

  const onSendFile = e => {
    setIsLoading(true)
    props.sendFile({
      file: file,
      fileRatio: fileRatio,
      after: messages[messages.length - 1].id,
      channelId: channelId,
      adminPlayerId: adminPlayerId
    })
    setFile(null)
    setFileSrc('')
    setFileRatio(1.0)
    fileInputRef.current.value = null
    props.setScrollAndLimit({scroll: 'bottom', limit: props.limit + 1})
  }

  const onSendMessage = e => {
    if (!message.trim().length) return

    setIsLoading(true)
    props.sendMessage({
      message: message,
      after: messages[messages.length - 1].id,
      channelId: channelId,
      adminPlayerId: adminPlayerId
    })
    setMessage('')
    composeRef.current.style.height = '0px'
    props.setScrollAndLimit({scroll: 'bottom', limit: props.limit + 1})
  }

  const onKey = e => {
    setFirstSeen(true)
    if (e.key == 'Delete' || e.key == 'Backspace') {
      setFile(null)
      setFileSrc('')
      setFileRatio(1.0)
    }

    if (e.key == 'Enter' && e.ctrlKey) setMessage(message + '\r\n')
    if (e.key == 'Enter' && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault()
      if (file) {
        onSendFile()
      } else {
        onSendMessage()
      }
    }
  }

  const onChange = e => {
    setMessage(e.target.value)
    composeRef.current.style.height = `${Math.min(260, composeRef.current.scrollHeight)}px`
  }

  const fileLoaded = e => {
    const files = e.target.files

    if (files && files[0]) {
      const file = files[0]
      if (file.size > 10000000) return window.alert('File size limit is 10MB')
      if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')

      readFile(file)
    }
  }

  const fileDropped = file => {
    if (file) {
      if (file.size > 10000000) return window.alert('File size limit is 10MB')
      readFile(file)
    }

    setDropzoneActive(false) 
  }

  const filePasted = e => {
    const file = e.clipboardData.files[0]
  
    if (file) {
      e.preventDefault()
      if (file.size > 10000000) return window.alert('File size limit is 10MB')
      if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')
      readFile(file)
    }
  }

  const readFile = f => {
    const fr = new FileReader()

    fr.onloadend = function() {
      setFileSrc(fr.result)
      setFile(f)

      var image = new Image()
      image.onload = function () {
        setFileRatio(this.width / this.height)
      }
      image.src = fr.result
    }
    fr.readAsDataURL(f)
  }

  const sendButton = () => {
    const proc = file ? onSendFile : onSendMessage
    
    return <button className='send-button' onClick={proc}>
      Send
      <i className='fa fa-solid fa-paper-plane'></i>
    </button>
  }

  return (
    <div className='chat' tabIndex={0} onKeyDown={onKey}>
      { isLoading && <div className='animated-spinner'></div> }
      { showTemplates && 
        <TemplatesModal onClose={() => setShowTemplates(false)} onSelect={(text) => setMessage(message + text)} /> 
      }
      <div className='messages' onWheelCapture={onWheelCapture} ref={messagesRef}>
        {messages.map((m, i) =>
          <Message
            key={m.id}
            index={i}
            imageUrl={players[m.sender].image_url || '/images/ic_user@2x.png'}
            player={{
              id: players[m.sender].id,
              name: `${sanitize(players[m.sender].first_name)} ${sanitize(players[m.sender].last_name)}`
            }}
            onShowPlayer={props.onShowPlayer}
            admin={m.sender == adminPlayerId}
            message={{
              type: m.type,
              payload: m.payload
            }}
            onDelete={() => i != 0 && props.deleteMessage({ channelId: channelId, id: m.id })}
          />
        )}
      </div>
      <div
        className='message-input'
        onDragEnter={() => setDropzoneActive(true)}
        onDragLeave={() => setDropzoneActive(false)}>
        {
          dropzoneActive && !file
          ? <Dropzone
              className='dropbox-container'
              accept='image/jpeg, image/png'
              onDrop={files => fileDropped(files[0])}>
                <span>Drop the image here</span>
            </Dropzone>
          : <textarea
              value={message}
              onChange={onChange}
              onPaste={e => filePasted(e)}
              onKeyPress={onKey}
              id='new-message'
              role='textarea'
              className='new-message'
              ref={composeRef}
            />
        }
        { file && <div className='info'>Press Delete to reset the image, Enter to send the image.</div> }
        <label htmlFor='upload'>
          <input
            id='upload'
            ref={fileInputRef}
            type='file' 
            accept='image/png,image/jpeg' 
            className='form-control'
            style={{display: 'none'}}
            onChange={fileLoaded}
          />
          <img className='photo-preview' src={fileSrc || '/images/messages/image.svg'} />
        </label>
        <img className='templates-button' src='/images/messages/template.svg' onClick={() => setShowTemplates(true)} />
        { sendButton() }
      </div>
    </div>
  )
}

FirestoreChannel.propTypes = {
  channelId: PropTypes.string,
  adminPlayerId: PropTypes.number,
  messages: PropTypes.array,
  players: PropTypes.array,
  sendMessage: PropTypes.func.isRequired,
  sendFile: PropTypes.func.isRequired,
  deleteMessage: PropTypes.func.isRequired,
  setScrollAndLimit: PropTypes.func.isRequired,
  loadMessages: PropTypes.func.isRequired,
  scroll: PropTypes.string,
  limit: PropTypes.number
}

export default FirestoreChannel
